import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { isUnlocked, tryUnlock, passcodeConfigured } from './passcode';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'clear'] as const;

/** Full-screen passcode gate. Renders children once unlocked (30-day window). */
export function PasscodeGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(() => isUnlocked());
  const [entered, setEntered] = useState('');
  const [error, setError] = useState(false);
  const locked = useRef(false);

  const submit = useCallback(async (code: string) => {
    locked.current = true;
    if (await tryUnlock(code)) {
      setUnlocked(true);
    } else {
      setError(true);
      setTimeout(() => {
        setEntered('');
        setError(false);
        locked.current = false;
      }, 500);
    }
  }, []);

  useEffect(() => {
    if (entered.length === 4 && !error) void submit(entered);
  }, [entered, error, submit]);

  const press = useCallback((k: string) => {
    if (unlocked || locked.current) return;
    if (k === 'clear') return setEntered('');
    if (!k) return;
    setEntered((prev) => (prev.length >= 4 ? prev : prev + k));
  }, [unlocked]);

  if (unlocked) return <>{children}</>;

  return (
    <div style={S.root}>
      <div style={S.card}>
        <div style={S.web} aria-hidden>🕸️</div>
        <div style={S.title}>Crack the Code</div>
        <div style={S.dots}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                ...S.dot,
                ...(error ? S.dotError : i < entered.length ? S.dotFilled : null),
              }}
            />
          ))}
        </div>
        <div style={S.pad}>
          {KEYS.map((k, i) => (
            <button
              key={i}
              type="button"
              onClick={() => press(k)}
              style={{
                ...S.key,
                ...(k === 'clear' ? S.keyClear : null),
                ...(k === '' ? S.keyBlank : null),
              }}
              aria-label={k === 'clear' ? 'Clear' : k || undefined}
            >
              {k === 'clear' ? '⌫' : k}
            </button>
          ))}
        </div>
        {!passcodeConfigured() && (
          <div style={S.hint}>Dev mode — any 4 digits unlock. Set VITE_PASSCODE_HASH to lock.</div>
        )}
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--frame)',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 26,
    padding: '36px 44px 30px',
    borderRadius: 28,
    background: 'var(--frame-2)',
    boxShadow: '0 20px 60px rgba(0,0,0,.5)',
  },
  web: { fontSize: 40, lineHeight: 1 },
  title: { fontSize: 30, fontWeight: 800, color: 'var(--gold)', letterSpacing: '.5px' },
  dots: { display: 'flex', gap: 18 },
  dot: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: 'var(--frame-edge)',
    transition: 'background .12s ease, transform .12s ease',
  },
  dotFilled: { background: 'var(--gold)', transform: 'scale(1.12)' },
  dotError: { background: 'var(--spidey-red)' },
  pad: { display: 'grid', gridTemplateColumns: 'repeat(3, 84px)', gap: 16 },
  key: {
    width: 84,
    height: 84,
    borderRadius: '50%',
    border: 'none',
    background: '#23315a',
    color: '#f4f4f4',
    fontSize: 34,
    fontWeight: 700,
    cursor: 'pointer',
  },
  keyClear: { background: 'transparent', color: '#8ca0c4', fontSize: 28 },
  keyBlank: { visibility: 'hidden' },
  hint: { fontSize: 13, color: '#8ca0c4', maxWidth: 280, textAlign: 'center', lineHeight: 1.4 },
};
